import styled from '@emotion/styled';
import { groupBy } from 'lodash';
import React from 'react';
import { Link, generatePath, useLocation } from 'react-router-dom';

import { editPatientRoute, patientRoute } from 'App/common/routeLookup';
import PatientDisplayId from 'App/components/PatientDisplayId/PatientDisplayId';
import PatientEngagement from 'App/components/PatientProfile/components/PatientEngagement/PatientEngagement';
import useGetPatientSubscriptions from 'App/components/PatientProfile/hooks/useGetPatientSubscriptions';
import useFetchPatientMedications from 'App/hooks/useFetchPatientMedications';
import useFetchPatientOtherDiagnoses from 'App/hooks/useFetchPatientOtherDiagnoses';
import useFetchPatientOtherMedications from 'App/hooks/useFetchPatientOtherMedications';
import useFetchPatientPredictiveModelRisk from 'App/hooks/useFetchPatientPredictiveModelRisk';
import { dateOnlySlashesFormat, formatUtcDate } from 'App/services/DateService';
import { formatPhone, isExpired } from 'App/utils';
import useIsPermitted from 'App/utils/hooks/useIsPermitted';
import { PERMISSION_TYPES } from 'App/utils/isPermitted/isPermitted';
import { patientPropType } from 'Shared/types';
import PatientHighRiskIndicator from '../../PatientHighRiskIndicator/PatientHighRiskIndicator';
import { PatientPredictiveModelRiskIndicator, PatientPredictiveModelRiskText } from '../../PatientPredictiveModelRisk';
import useFetchPatientAllergies from '../hooks/useFetchPatientAllergies';
import useFetchPatientICD10 from '../hooks/useFetchPatientICD10';
import PatientProfileNotes from 'App/components/PatientProfileNotes/PatientProfileNotes';
import PatientImportantNotesIndicator from 'App/components/PatientImportantNotesIndicator';
import PatientLabels from 'App/components/PatientLabels';
import CollapsiblePatientCardSection from 'App/components/CollapsiblePatientCardSection';
import PatientImportantNotesAccordion from 'App/components/PatientImportantNotesAccordion';
import PatientAttachments from 'App/components/PatientAttachments';
import SidebarMedicationAccordion from './SidebarMedicationAccordion';
import AssessmentSidebarEngagementSubscribed from './AssessmentSidebarEngagementSubscribed';
import EmergencyContactSection from './EmergencyContactSection';
import { BottomGutter, PatientCardLabel } from 'App/components/Assessments/sidebar/styles/PatientCard';

const SidebarStyle = styled.div`
    background-color: var(--glass);
    padding: 20px;
    width: 300px;
    flex-shrink: 0;
    overflow: auto;
`;

const PatientName = styled.div`
    font-size: 18px;
`;

const LeftGutterStyle = styled.div`
    padding-left: 1.5em;
`;

const LeftButtonWrapper = styled.div`
    button.action-button-link.action-button-link {
        padding-left: 0;
    }
`;

const PredictiveModelTextStyledDiv = styled.div`
    font-size: 12px;
    background-color: var(--white);
    border-radius: 4px;
    display: block;
    margin: 10px 0;
    padding: 2px;
`;

const AssessmentSidebar = ({ patient }) => {
    const location = useLocation();
    const patientPredictiveModelEnabled = useIsPermitted(PERMISSION_TYPES.PATIENT_PREDICTIVE_MODEL);
    const isPatientMessagingConf = useIsPermitted(PERMISSION_TYPES.PATIENT_MESSAGING_CONF);
    const { data: icd10s } = useFetchPatientICD10(patient.id);
    const { data: patientMedications } = useFetchPatientMedications(patient.id);
    const patientMedicationsList = Object.values(patientMedications || {})
        .filter((patientMed) => !isExpired(patientMed.status[0]?.end))
        .sort((a, b) => (a?.medication?.name || '').localeCompare(b?.medication?.name || ''));
    const { data: allergies } = useFetchPatientAllergies(patient.id);
    const { data: otherDiagnoses } = useFetchPatientOtherDiagnoses(patient.id);
    const otherDiagnosesList = Object.values(otherDiagnoses || []);
    const { data: otherMedications } = useFetchPatientOtherMedications(patient.id);
    const otherMedicationsList = Object.values(otherMedications || []);
    const patientSubscriptions = useGetPatientSubscriptions(patient.id);
    const { patientMessagingSubscription, triggerSubscriptionRefetch } = patientSubscriptions;
    const { data: predictiveModel } = useFetchPatientPredictiveModelRisk(patient.id, patientPredictiveModelEnabled);
    const hasPredictiveModelRiskData = predictiveModel?.length > 0;

    return (
        <SidebarStyle className="assessment-sidebar">
            <PatientName>
                <Link
                    to={{ pathname: patient.id && generatePath(patientRoute, { patientId: patient.id }) }}
                    style={{ marginRight: '10px' }}
                >
                    {patient.lastName}, {patient.firstName}
                </Link>
                {patient.highRisk && <PatientHighRiskIndicator patientId={patient.id} />}
                {patient?.id && <PatientImportantNotesIndicator patientId={patient.id} />}
                {hasPredictiveModelRiskData && (
                    <>
                        <PatientPredictiveModelRiskIndicator riskSegments={predictiveModel} />
                        <PredictiveModelTextStyledDiv>
                            <PatientPredictiveModelRiskText riskSegments={predictiveModel} />
                        </PredictiveModelTextStyledDiv>
                    </>
                )}
            </PatientName>

            <div>
                {patient.preferredName && `"${patient.preferredName}" `}
                {patient.pronouns && `(${patient.pronouns}) `}
                {patient.id && <PatientDisplayId patientId={patient.id} />}
            </div>

            <BottomGutter>
                <Link
                    to={{
                        pathname: patient.id && generatePath(editPatientRoute, { patientId: patient.id }),
                        search: `redirectUrl=${location.pathname}`,
                    }}
                >
                    Edit Patient
                </Link>
            </BottomGutter>

            {patient.id && <PatientLabels patientId={patient.id} />}

            {patient.id && (
                <LeftButtonWrapper>
                    <PatientAttachments patientId={patient.id} />
                </LeftButtonWrapper>
            )}

            <CollapsiblePatientCardSection title="Demographics">
                {patient.status && (
                    <div>
                        <PatientCardLabel>Status</PatientCardLabel> <span>{patient.status}</span>
                        <span> {formatUtcDate({ date: patient.deceasedDate, format: dateOnlySlashesFormat })}</span>
                    </div>
                )}
                <div>
                    <PatientCardLabel>Sex</PatientCardLabel> <span>{patient.gender}</span>
                </div>
                {patient.genderIdentity && (
                    <div>
                        <PatientCardLabel>Gender Identity</PatientCardLabel> <span>{patient.genderIdentity}</span>
                    </div>
                )}
                <div>
                    <PatientCardLabel>DOB</PatientCardLabel>{' '}
                    <span>{formatUtcDate({ date: patient.birthDate, format: dateOnlySlashesFormat })}</span>
                </div>
                {patient.height && (
                    <div>
                        <PatientCardLabel>Height</PatientCardLabel> <span>{patient.height} in</span>
                    </div>
                )}
                {patient.weight && (
                    <div>
                        <PatientCardLabel>Weight</PatientCardLabel> <span>{patient.weight} lbs</span>
                    </div>
                )}
            </CollapsiblePatientCardSection>

            <CollapsiblePatientCardSection title="Patient Notes">
                <PatientProfileNotes patientId={patient?.id} />
                {patient?.id && <PatientImportantNotesAccordion patientId={patient.id} initialVisible />}
            </CollapsiblePatientCardSection>

            <CollapsiblePatientCardSection title="Contact Information">
                <BottomGutter>
                    <PatientCardLabel>Address</PatientCardLabel>
                    <div>
                        {patient.addressLine1} {patient.addressLine2}
                    </div>
                    <div>
                        {patient.city}, {patient.state} {patient.zip}
                    </div>
                </BottomGutter>

                {patient.languageSpoken && (
                    <BottomGutter>
                        <PatientCardLabel>Preferred Language</PatientCardLabel>
                        <div>{patient.languageSpoken}</div>
                    </BottomGutter>
                )}

                {patient.homePhone && (
                    <div>
                        <PatientCardLabel>H</PatientCardLabel> {formatPhone(patient.homePhone)}
                        {patient.preferredContactMethod === 'Home Phone' && ' (preferred)'}
                    </div>
                )}
                {patient.mobilePhone && (
                    <div>
                        <PatientCardLabel>M</PatientCardLabel> {formatPhone(patient.mobilePhone)}
                        {patient.preferredContactMethod === 'Mobile Phone' && ' (preferred)'}
                    </div>
                )}
                {patient.workPhone && (
                    <div>
                        <PatientCardLabel>W</PatientCardLabel> {formatPhone(patient.workPhone)}
                        {patient.preferredContactMethod === 'Work Phone' && ' (preferred)'}
                    </div>
                )}
                {patient.email && (
                    <div>
                        <PatientCardLabel>E</PatientCardLabel> {patient.email}
                        {patient.preferredContactMethod === 'Email' && ' (preferred)'}
                    </div>
                )}

                <EmergencyContactSection patient={patient} />
            </CollapsiblePatientCardSection>

            {isPatientMessagingConf && (
                <CollapsiblePatientCardSection title="Patient Engagement">
                    <PatientEngagement
                        patientDemographicsData={patient}
                        patientMessagingSubscription={patientMessagingSubscription}
                        refetchMessagingSubscriptions={triggerSubscriptionRefetch}
                        subscribedComponent={AssessmentSidebarEngagementSubscribed}
                    />
                </CollapsiblePatientCardSection>
            )}

            {!allergies ? null : (
                <CollapsiblePatientCardSection title="Allergies">
                    {!!Number(patient.noKnownAllergies) && 'No Known Allergies'}
                    {!allergies.length && !Number(patient.noKnownAllergies) && '--'}
                    {allergies.map((allergy, index) => (
                        // eslint-disable-next-line react/no-array-index-key
                        <div key={index}>{allergy}</div>
                    ))}
                </CollapsiblePatientCardSection>
            )}

            <CollapsiblePatientCardSection title="Medications">
                {Object.values(groupBy(patientMedicationsList, 'medication.name')).map((medList) => (
                    <SidebarMedicationAccordion
                        key={medList[0].medication.id}
                        medList={medList}
                        patientId={patient.id}
                    />
                ))}
            </CollapsiblePatientCardSection>

            {!icd10s ? null : (
                <CollapsiblePatientCardSection title="Diagnoses">
                    {icd10s.length === 0
                        ? '--'
                        : icd10s.map((icd10) => (
                              <div key={icd10.icd10Details.DxPk}>
                                  {icd10.icd10Details.DxCode} {icd10.icd10Details.DxDescShort}
                              </div>
                          ))}
                </CollapsiblePatientCardSection>
            )}

            <CollapsiblePatientCardSection title="Other Medications">
                {otherMedications && !otherMedicationsList.length
                    ? '--'
                    : otherMedicationsList.map((otherMedication) => (
                          <div key={otherMedication.id}>
                              <div>{otherMedication.text}</div>
                              <LeftGutterStyle>
                                  {otherMedication.routeOfAdmin && (
                                      <div>
                                          <PatientCardLabel>Route</PatientCardLabel> {otherMedication.routeOfAdmin}
                                      </div>
                                  )}
                                  {otherMedication.dose && (
                                      <div>
                                          <PatientCardLabel>Strength</PatientCardLabel> {otherMedication.dose}
                                      </div>
                                  )}
                                  {otherMedication.frequency && (
                                      <div>
                                          <PatientCardLabel>Frequency</PatientCardLabel> {otherMedication.frequency}
                                      </div>
                                  )}
                              </LeftGutterStyle>
                          </div>
                      ))}
            </CollapsiblePatientCardSection>

            {!otherDiagnoses ? null : (
                <CollapsiblePatientCardSection title="Other Diagnoses">
                    {!otherDiagnosesList.length
                        ? '--'
                        : otherDiagnosesList.map((otherDiagnosis) => (
                              <div key={otherDiagnosis.id}>{otherDiagnosis.text}</div>
                          ))}
                </CollapsiblePatientCardSection>
            )}

            <CollapsiblePatientCardSection title="Welcome Packet">
                {patient.welcomePacket ? <div>Requested</div> : <div>Not Requested</div>}
                {patient.welcomePacketSent && (
                    <div>
                        <PatientCardLabel>Mail Date</PatientCardLabel>{' '}
                        <span>{formatUtcDate({ date: patient.welcomePacketSent, format: dateOnlySlashesFormat })}</span>
                    </div>
                )}
                {patient.welcomePacketReturned && (
                    <div>
                        <PatientCardLabel>Return Date</PatientCardLabel>{' '}
                        <span>
                            {formatUtcDate({ date: patient.welcomePacketReturned, format: dateOnlySlashesFormat })}
                        </span>
                    </div>
                )}
            </CollapsiblePatientCardSection>
        </SidebarStyle>
    );
};

AssessmentSidebar.propTypes = {
    patient: patientPropType.isRequired,
};

export default AssessmentSidebar;
